import { I18nDisplayName } from "@/00-shared/type";
import { z } from "zod";
import { CascaderOption, Operator, basicFilterType } from "./entity";
import qs from "qs";
import {
  JOINT_LABEL,
  shouldShowPercent,
  shouldConvertDuration,
} from "@/03-presenters/widgets/entity/CustomSelectCompV2";
import { Filter } from "@/03-presenters/widgets/TableStoreProvider";
import { TIME_JOINT_LABEL } from "@/03-presenters/widgets/entity/CustomizedDatePickerV2";
import { INPUT_TAG_JOIN_KEY } from "@/03-presenters/widgets/entity/InputTagComp";

export enum FilterOptionValueDisplayStyle {
  TILE = 1, // toggle group 例如国家
  DROPDOWN = 2, // 下拉菜单
  CHECKBOX = 3, // checkbox
  CASCADER = 4, // 级联选择
  OPTION = 5, // 单选
  SWITCH = 6, // switch
  TIME = 7,
  TIMERANGE = 8,
  TEXTDROPDOWN = 9,
}

export enum FilterType {
  SINGLE_FIELD_VALUE_BASED = 1,
  OPTION_BASED = 2,
}

export const getAvailableOptionsFieldKey = (filter_type: FilterType) => {
  if (filter_type === FilterType.SINGLE_FIELD_VALUE_BASED) {
    return "field_rendered_value_list" as const;
  }
  if (filter_type === FilterType.OPTION_BASED) {
    return "field_quick_option_name_list" as const;
  }
  return null;
};

const filter_field = z.object({
  filter_list: z
    .object({
      filter_key: z.string(),
      filter_type: z.nativeEnum(FilterType),
      filter_display_name: I18nDisplayName,
      allow_multiple_choice: z.boolean(),
      filter_option_value_display_style: z.nativeEnum(
        FilterOptionValueDisplayStyle,
      ),
      show_search_bar: z.boolean(),
    })
    .array(),
  group_name: I18nDisplayName,
});

const AvailableOptionsTransformOfQuick = z.object({
  field_key: z.string().optional(),
  filter_key: z.string(),
  field_quick_option_name_list: z.array(
    z.object({
      zh: z.string().optional(),
      en: z.string().optional(),
      label: z.string(),
    }),
  ),
});
const AvailableOptionsTransformOfRender = z.object({
  field_key: z.string().optional(),
  filter_key: z.string(),
  field_rendered_value_list: z.array(
    z.object({
      cascaderOption: CascaderOption.optional(),
      // transform country to label like
      label: z.string().optional(),
      zh: z.string().optional(),
      en: z.string().optional(),
    }),
  ),
});
const AvailableOptionsTransformCountry = z.union([
  AvailableOptionsTransformOfQuick,
  AvailableOptionsTransformOfRender,
]);
// 获取 field_rendered_value_list 的类型
export type AvailableOptions = z.infer<typeof AvailableOptionsTransformCountry>;
const filter_field_with_options = z.object({
  filter_list: z
    .object({
      filter_key: z.string(),
      filter_type: z.nativeEnum(FilterType),
      filter_display_name: I18nDisplayName,
      allow_multiple_choice: z.boolean(),
      filter_option_value_display_style: z.nativeEnum(
        FilterOptionValueDisplayStyle,
      ),
      show_search_bar: z.boolean(),
      available_options: AvailableOptionsTransformCountry,
    })
    .array(),
  group_name: I18nDisplayName,
});

export enum AtomicFieldType {
  TEXT = 1,
  URL = 2,
  NUMBER = 3,
  TIMESTAMP = 4,
  DATETIME = 5,
  CHECKBOX = 6,
  OPTION = 7,
  CASCADER = 8,
  CURRENCY = 9,
  IMAGE = 10,
  NUMBER_SERIES = 11,
  CURRENCY_SERIES = 12,
  COUNTRY = 13,
  ENUMERATOR = 14,
  ENTITY_ID = 15,
  SKU = 16,
  PRICE = 17,
  SELLER_PROP = 18,
  JSON_ = 19,
  CONDITION_NUMBER = 20,
  Record_URL = 21,
  REVIEW_TAGS = 22,
  TREND = 23,
  PARTNERED_BRAND = 24,
  REVIEWOPINION = 25,
}

enum FieldConfigType {
  SYSTEM_DEFAULT = 1,
  CUSTOMIZED = 2,
}
export const entity_field_type = z.object({
  atomic_field_type: z.nativeEnum(AtomicFieldType).optional(),
  array: z
    .object({
      atomic_field: z.nativeEnum(AtomicFieldType),
    })
    .optional(),
  analytics: z
    .object({
      sub_field: z.record(z.nativeEnum(AtomicFieldType)),
    })
    .optional(),
  time_series: z
    .object({
      value_type: z.nativeEnum(AtomicFieldType),
    })
    .optional(),
});

export const fieldType = z.object({
  field_key: z.string(),
  field_display_name: I18nDisplayName,
  field_description: I18nDisplayName,
  entity_field_type,
  allow_filter: z.boolean(),
  allow_fuzzy_search: z.boolean(),
  allow_sort: z.boolean(),
  allow_ranking_sort: z.boolean().optional(),
  field_config_type: z.nativeEnum(FieldConfigType),
});

export type fieldType = z.infer<typeof fieldType>;

export enum groupDisplayType {
  NORMAL = 1,
  IMAGE_WITH_DESC = 2,
  AVATAR_WITH_DESC = 3,
  VIDEO_WITH_DESC = 4,
  AVATAR_WITH_SUBCRIPT_AND_DESC = 5,
  AVATAR_WITH_DESC_WITH_VERIFY_SIGN = 6,
  IMAGE_GROUP = 7,
  VIDEO_GROUP = 8,
  FROM_A_TO_B = 9,
  BUTTON_GROUP = 10,
  PRODUCT_INFO_GROUP = 12,
  SELLER_INFO_GROUP = 13,
  CREATOR_CATEGORY_GROUP = 14,
  CREATOR_PRODUCT_GROUP = 15,
  Trend = 16,
  RELATEDLIVE = 17,
  RELATEDVIDEO = 18,
}
export const table_field = z.object({
  field_list: fieldType.array(),
  group_name: I18nDisplayName,
  group_display_type: z.nativeEnum(groupDisplayType),
  info: z.string().optional(),
});

export type table_field = z.infer<typeof table_field>;

export enum SortOrder {
  ASC = 0,
  DESC = 1,
}

const sort_config = z.object({
  field: z.string(),
  sort_order: z.nativeEnum(SortOrder),
});

export const ViewDetail = z.object({
  table_view: z.object({
    view_name: z.string(),
    view_display_name: I18nDisplayName,
    keyword_search_field_list: z.string().array(),
    filter_field_list: filter_field.array(),
    table_field_list: table_field.array(),
    sort_config: sort_config.array(),
  }),
  entity_type_name: z.string(), // 自定义字段，非接口返回，方便前端使用
});

export type ViewDetail = z.infer<typeof ViewDetail>;

const levelMap: Record<string, string> = {
  "1": "product_first_category",
  "2": "product_second_category",
  "3": "product_third_category",
};
export const buildSearchEntityBasicFilter = (
  viewDetail: ViewDetail,
  searchParams: string,
) => {
  const parsedParams = qs.parse(searchParams);
  const basicFilter: basicFilterType = [];
  viewDetail.table_view.filter_field_list.forEach((filterField) => {
    filterField.filter_list.forEach((filter) => {
      const filterVal = parsedParams[filter.filter_key];
      if (!filterVal || filterVal === "_ALL_") {
        return;
      }
      if (typeof filterVal === "string" && filterVal.includes(JOINT_LABEL)) {
        const [startValue, endValue] = filterVal.split(JOINT_LABEL);
        const [operator, value] = (() => {
          if (startValue && endValue) {
            if (shouldShowPercent(filter.filter_key)) {
              return [
                Operator.CLOSED_OPEN_INTERVAL,
                [Number(startValue) / 100, Number(endValue) / 100],
              ];
            }
            if (shouldConvertDuration(filter.filter_key)) {
              return [
                Operator.CLOSED_OPEN_INTERVAL,
                [Number(startValue) * 1000, Number(endValue) * 1000],
              ];
            }
            return [Operator.CLOSED_OPEN_INTERVAL, [startValue, endValue]];
          }

          if (startValue) {
            if (shouldShowPercent(filter.filter_key)) {
              return [Operator.GREATER, [Number(endValue) / 100]];
            }
            if (shouldConvertDuration(filter.filter_key)) {
              return [Operator.GREATER, [Number(endValue) * 1000]];
            }
            return [Operator.GREATER, [startValue]];
          }

          if (shouldShowPercent(filter.filter_key)) {
            return [Operator.LESS, [Number(endValue) / 100]];
          }
          if (shouldConvertDuration(filter.filter_key)) {
            return [Operator.LESS, [Number(endValue) * 1000]];
          }
          return [Operator.LESS, [endValue]];
        })();
        basicFilter.push({
          // TODO: 让后端把field_key也传过来，然后用fieldkey
          filter_key: filter.filter_key,
          operator,
          value,
        });
        return;
      }
      const isValueArray = Array.isArray(filterVal);
      let key: "value" | "filter_option_name" | null = null;
      const isValueBased =
        filter.filter_type === FilterType.SINGLE_FIELD_VALUE_BASED;
      const isOptionBased = filter.filter_type === FilterType.OPTION_BASED;
      if (isValueBased) {
        key = "value";
      }
      if (isOptionBased) {
        key = "filter_option_name";
      }
      if (!key) {
        return;
      }
      basicFilter.push({
        // TODO: 让后端把field_key也传过来，然后用fieldkey
        field_key: isValueBased ? filter.filter_key : undefined,
        filter_key: isOptionBased ? filter.filter_key : undefined,
        operator: isValueBased ? Operator.IN : undefined,
        [key]:
          isValueArray || isOptionBased
            ? (filterVal as string[])
            : [filterVal as string],
      });
    });
  });
  if (parsedParams?.stupid_back_end) {
    const selectedValue = parsedParams.stupid_back_end as [""];
    if (typeof selectedValue?.[0] === "string") {
      const [id, level] = selectedValue?.[0]?.split("_");
      if (id && level) {
        basicFilter.push({
          field_key: levelMap[level],
          operator: Operator.EQUAL,
          value: id,
        });
      }
    }
  }
  return basicFilter;
};
export const buildSearchEntityBasicFilterV2 = (filters: Filter[]) => {
  return filters
    .filter((filter) => filter.value && filter.value !== "_ALL_")
    .map((filter) => {
      const { filterKey, value } = filter;
      if (value.includes(JOINT_LABEL)) {
        const [startValue, endValue] = value.split(JOINT_LABEL);
        if (shouldShowPercent(filterKey)) {
          return {
            filter_key: filterKey,
            left_value: startValue ? Number(startValue) / 100 : undefined,
            right_value: endValue ? Number(endValue) / 100 : undefined,
          };
        }
        if (shouldConvertDuration(filterKey)) {
          return {
            filter_key: filterKey,
            left_value: startValue ? Number(startValue) * 1000 : undefined,
            right_value: endValue ? Number(endValue) * 1000 : undefined,
          };
        }
        return {
          filter_key: filterKey,
          left_value: startValue ? Number(startValue) : undefined,
          right_value: endValue ? Number(endValue) : undefined,
        };
      }
      if (value.includes(TIME_JOINT_LABEL)) {
        const [from, to] = value.split(TIME_JOINT_LABEL);
        return {
          filter_key: filterKey,
          left_date_time: from.replaceAll("-", ""),
          right_date_time: to ? to.replaceAll("-", "") : undefined,
        };
      }
      if (value.includes(INPUT_TAG_JOIN_KEY)) {
        const [includeValue, excludeValue] = value.split(INPUT_TAG_JOIN_KEY);
        return {
          filter_key: filterKey,
          include_values: includeValue ? includeValue.split(",") : undefined,
          exclude_values: excludeValue ? excludeValue.split(",") : undefined,
        };
      }
      return {
        filter_key: filterKey,
        quick_option_name: value,
      };
    });
};
export const ViewDetailWithFilterOptions = z.object({
  table_view: z.object({
    view_name: z.string(),
    view_display_name: I18nDisplayName,
    keyword_search_field_list: z.string().array(),
    filter_field_list: filter_field_with_options.array(),
    table_field_list: table_field.array(),
    sort_config: sort_config.array(),
  }),
  entity_type_name: z.string(), // 自定义字段，非接口返回，方便前端使用
});

export type ViewDetailWithFilterOptions = z.infer<
  typeof ViewDetailWithFilterOptions
>;
